import React from 'react';
import cn from 'classnames';
import ReactTooltip from 'react-tooltip';
import { AssetVersionsSummary } from 'src/types/TAssets';
import styles from './VersionBadge.module.scss';

type VersionBadgeProps = {
  versions?: AssetVersionsSummary | null;
  className?: string;
  /** smaller pills for the list row */
  compact?: boolean;
};

/**
 * Content versioning affordances on asset cards: the live version number when
 * the asset has other versions, and a "pending release" pill when a version is
 * waiting for someone to make it live. Renders nothing for an asset that has
 * only ever had one version.
 */
export const VersionBadge: React.FC<VersionBadgeProps> = ({ versions, className, compact }) => {
  if (!versions || (!versions.count && !versions.pending)) return null;
  const previous = versions.count - (versions.pending ? 1 : 0);
  const tip =
    `Version ${versions.live} is live` +
    (previous > 0 ? `; ${previous} previous version${previous === 1 ? '' : 's'} kept` : '') +
    (versions.pending ? `; version ${versions.pending} is waiting for release` : '');
  return (
    <span className={cn(styles.wrapper, { [styles.compact]: compact }, className)} data-tip={tip} aria-label={tip}>
      <span className={styles.version} data-test="version-badge">
        v{versions.live}
      </span>
      {versions.pending ? (
        <span className={styles.pending} data-test="version-pending-badge">
          v{versions.pending} pending
        </span>
      ) : null}
      <ReactTooltip effect="solid" />
    </span>
  );
};

export default VersionBadge;
